import React, { useState, useEffect } from "react";
import {
  useNavigate,
  useParams,
  useSearchParams,
  Link,
} from "react-router-dom";
import { toast } from "react-toastify";
import { useBuilding } from "../../context/BuildingContext";
import {
  bankTransactionsApi,
  financialRecordsApi,
  categoriesApi,
} from "../../services/api";
import PageHeader from "../../components/common/PageHeader";
import ErrorAlert from "../../components/common/ErrorAlert";
import Spinner from "../../components/common/Spinner";
import {
  BankTransaction,
  BankTransactionType,
} from "@akfdev-com/shared/src/models";

interface TransactionForm {
  description: string;
  date: string;
  type: BankTransactionType;
  grossValue: string;
  reference: string;
  reconciled: boolean;
  categoryName: string;
  nominalCode: string;
  financialRecordId: string;
}

interface FinancialRecordOption {
  id: string;
  label: string;
  type: string;
  status: string;
  date: string;
  amount: number;
}

const BankTransactionEdit: React.FC = () => {
  const navigate = useNavigate();
  const { txId } = useParams<{ id: string; txId: string }>();
  const [searchParams] = useSearchParams();
  const { currentBuilding } = useBuilding();
  const buildingId =
    searchParams.get("buildingId") || currentBuilding?.id || "";
  const bankAccountId = searchParams.get("bankAccountId") ?? "";
  const bankAccountName = searchParams.get("bankAccountName") ?? "";

  const [form, setForm] = useState<TransactionForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Categories
  const [categories, setCategories] = useState<
    { nominalCode: string; name: string }[]
  >([]);

  // Financial record linking
  const [recordOptions, setRecordOptions] = useState<FinancialRecordOption[]>(
    [],
  );
  const [recordSearch, setRecordSearch] = useState("");
  const [linkedRecord, setLinkedRecord] =
    useState<FinancialRecordOption | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tx, cats] = await Promise.all([
          bankTransactionsApi.get(
            txId!,
            buildingId,
          ) as Promise<BankTransaction>,
          categoriesApi.getAll(),
        ]);
        setForm({
          description: tx.description,
          date: tx.date,
          type: tx.type,
          grossValue: (tx.grossValue / 100).toString(),
          reference: tx.reference || "",
          reconciled: tx.reconciled,
          categoryName: tx.categoryName || "",
          nominalCode: tx.nominalCode || "",
          financialRecordId: tx.financialRecordId || "",
        });
        setCategories(Array.isArray(cats) ? cats : []);

        // Load financial records for the building
        if (buildingId) {
          const records = await financialRecordsApi.getAll(buildingId);
          const opts: FinancialRecordOption[] = (
            Array.isArray(records) ? records : []
          ).map((r: any) => ({
            id: r.id,
            label: [
              r.type,
              r.invoiceNumber ||
                r.title ||
                r.description ||
                r.reference ||
                r.unitName,
              r.date,
            ]
              .filter(Boolean)
              .join(" — "),
            type: r.type,
            status: r.status,
            date: r.date,
            amount: r.amount ?? 0,
          }));
          setRecordOptions(opts);

          // If already linked, pre-populate the linked display
          if (tx.financialRecordId) {
            const found = opts.find((o) => o.id === tx.financialRecordId);
            if (found) setLinkedRecord(found);
          }
        }
      } catch (err) {
        setError("Failed to load transaction.");
      } finally {
        setLoading(false);
      }
    };
    if (txId && buildingId) fetchData();
  }, [txId, buildingId]);

  const handleChange = (
    e: React.ChangeEvent<
      InputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm(
        (prev) =>
          prev && { ...prev, [name]: (e.target as HTMLInputElement).checked },
      );
    } else {
      setForm((prev) => prev && { ...prev, [name]: value });
    }
  };

  // When a category is selected by name, also populate the nominal code
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const name = e.target.value;
    const cat = categories.find((c) => c.name === name);
    setForm((prev) =>
      prev
        ? {
            ...prev,
            categoryName: name,
            nominalCode: cat?.nominalCode || prev.nominalCode,
          }
        : prev,
    );
  };

  const handleLinkRecord = (opt: FinancialRecordOption) => {
    setLinkedRecord(opt);
    setForm((prev) => prev && { ...prev, financialRecordId: opt.id });
    setRecordSearch("");
  };

  const handleUnlinkRecord = () => {
    setLinkedRecord(null);
    setForm((prev) => prev && { ...prev, financialRecordId: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !buildingId || !bankAccountId || !txId) {
      setError("Missing context or form data.");
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await bankTransactionsApi.update(txId, {
        ...form,
        grossValue: Math.round(parseFloat(form.grossValue || "0") * 100),
        bankAccountId,
        bankAccountName,
        buildingId,
        financialRecordId: form.financialRecordId || undefined,
      });
      toast.success("Transaction updated successfully.");
      navigate(`/dashboard/banking/${bankAccountId}?buildingId=${buildingId}`);
    } catch (err) {
      setError("Failed to update transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorAlert message={error} />;
  if (!form) return null;

  const filteredOptions = recordOptions.filter((o) => {
    if (!recordSearch) return true;
    return o.label.toLowerCase().includes(recordSearch.toLowerCase());
  });

  const TYPE_BADGE: Record<string, string> = {
    invoice: "bg-blue-100 text-blue-700",
    bill: "bg-purple-100 text-purple-700",
    payment: "bg-teal-100 text-teal-700",
  };

  return (
    <div>
      <PageHeader
        title="Edit Transaction"
        description={
          bankAccountName
            ? `Account: ${bankAccountName}`
            : "Edit transaction entry"
        }
      />
      <form
        onSubmit={handleSubmit}
        className="mt-6 bg-white shadow rounded-lg p-4 sm:p-6 space-y-6 w-full max-w-2xl"
      >
        {/* ── Core fields ───────────────────────────────────────── */}
        <section>
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Transaction Details
          </h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="description"
              >
                Description <span className="text-red-500">*</span>
              </label>
              <input
                id="description"
                name="description"
                type="text"
                required
                value={form.description}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="date"
              >
                Date <span className="text-red-500">*</span>
              </label>
              <input
                id="date"
                name="date"
                type="date"
                required
                value={form.date}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="type"
              >
                Type
              </label>
              <select
                id="type"
                name="type"
                value={form.type}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
              >
                {Object.values(BankTransactionType).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.replace(/([A-Z])/g, " $1").trim()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="grossValue"
              >
                Gross Value (£)
              </label>
              <p className="text-xs text-gray-400">
                Positive = money in, negative = money out
              </p>
              <input
                id="grossValue"
                name="grossValue"
                type="number"
                step="0.01"
                value={form.grossValue}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
              />
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="reference"
              >
                Reference
              </label>
              <input
                id="reference"
                name="reference"
                type="text"
                value={form.reference}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input
                id="reconciled"
                name="reconciled"
                type="checkbox"
                checked={form.reconciled}
                onChange={handleChange}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="reconciled" className="text-sm text-gray-700">
                Reconciled
              </label>
            </div>
          </div>
        </section>

        {/* ── Category / Nominal Code ───────────────────────────── */}
        <section className="border-t border-gray-100 pt-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Category &amp; Nominal Code
          </h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="categoryName"
              >
                Category
              </label>
              {categories.length > 0 ? (
                <select
                  id="categoryName"
                  name="categoryName"
                  value={form.categoryName}
                  onChange={handleCategoryChange}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
                >
                  <option value="">— None —</option>
                  {categories.map((c) => (
                    <option key={c.nominalCode} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="categoryName"
                  name="categoryName"
                  type="text"
                  value={form.categoryName}
                  onChange={handleChange}
                  placeholder="e.g. Maintenance"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
                />
              )}
            </div>
            <div>
              <label
                className="block text-sm font-medium text-gray-700"
                htmlFor="nominalCode"
              >
                Nominal Code
              </label>
              <input
                id="nominalCode"
                name="nominalCode"
                type="text"
                value={form.nominalCode}
                onChange={handleChange}
                placeholder="e.g. 7200"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
              />
            </div>
          </div>
        </section>

        {/* ── Financial Record Link ─────────────────────────────── */}
        <section className="border-t border-gray-100 pt-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">
            Linked Financial Record
          </h3>
          <p className="text-xs text-gray-400 mb-4">
            Link this transaction to an invoice, bill, or service charge. The
            link uses the record's unique ID — it remains valid even if the
            record type changes.
          </p>

          {linkedRecord ? (
            <div className="flex items-start justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                      TYPE_BADGE[linkedRecord.type] ??
                      "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {linkedRecord.type}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      linkedRecord.status === "paid" ||
                      linkedRecord.status === "completed"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {linkedRecord.status}
                  </span>
                </div>
                <p className="text-sm text-gray-900 font-medium mt-1">
                  {linkedRecord.label}
                </p>
                <p className="text-xs text-gray-500">ID: {linkedRecord.id}</p>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <Link
                  to={`/dashboard/financial-records/${linkedRecord.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:underline"
                >
                  View →
                </Link>
                <button
                  type="button"
                  onClick={handleUnlinkRecord}
                  className="text-xs text-red-500 hover:text-red-700 hover:underline"
                >
                  Unlink
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Search records by description, invoice #, type…"
                value={recordSearch}
                onChange={(e) => setRecordSearch(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {recordSearch && (
                <div className="border border-gray-200 rounded-md overflow-hidden max-h-52 overflow-y-auto">
                  {filteredOptions.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-gray-400">
                      No records found
                    </p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {filteredOptions.slice(0, 20).map((opt) => (
                        <li key={opt.id}>
                          <button
                            type="button"
                            onClick={() => handleLinkRecord(opt)}
                            className="w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-primary-50 text-left"
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                                  TYPE_BADGE[opt.type] ??
                                  "bg-gray-100 text-gray-600"
                                }`}
                              >
                                {opt.type}
                              </span>
                              <span className="truncate text-gray-800">
                                {opt.label}
                              </span>
                            </span>
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                              £{((opt.amount ?? 0) / 100).toFixed(2)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {!recordSearch && (
                <p className="text-xs text-gray-400">
                  Start typing to search for a financial record.
                </p>
              )}
            </div>
          )}
        </section>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px] sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            {submitting ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Work around TypeScript narrowing: ChangeEvent target can be input or select
type InputElement = HTMLInputElement;

export default BankTransactionEdit;
